import React, { useEffect, useState } from 'react';
import {
  RiExchangeLine,
  RiMoneyDollarCircleLine,
  RiBankCardLine,
  RiFileList3Line,
  RiHomeOfficeFill,
  RiArrowUpLine,
  RiArrowDownLine,
  RiMoneyEuroBoxLine,
  RiCoinsLine,
  RiCalendar2Line,
} from 'react-icons/ri';
import { BsCalendar2Check, BsBuilding, BsSearch } from 'react-icons/bs';
import { RxDividerHorizontal } from "react-icons/rx";
import { TbZoomReset } from 'react-icons/tb';
import { FaFileExcel, FaFilePdf } from "react-icons/fa";
import './index.css';
import Spinner from '../../../../components/Spinner';
import { useCash } from '../../../../hooks/useCash';
import NotifyC from '../../../../components/Notifications';
import SelectSearch from '../../../../components/ui/SelectSearch';
import { getUser, exportMultipleTablesExcel, sqlDateFormat, exportMultipleTablesPDF, formatCurrency} from '../../../../utils/Common';
import { useSystem } from '../../../../hooks/useSystem';

const SearchDailyReport = () => {
  const user = getUser();
  const { searchDailyReport, getCashBoxStatus } = useCash();
  const { Logs } = useSystem();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCaja, setSelectedCaja] = useState('');
  const [listCashBox, setListCashBox] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  const [hasSearched, setHasSearched] = useState(false);
  const [activeReportIndex, setActiveReportIndex] = useState(0); 
  const [reports, setReports] = useState([]); 
   // Calcular totales
  const checksTotal = reports[activeReportIndex]?.checks.reduce((sum, check) => sum + (check.amount || 0), 0) || 0;
  const grandTotal = (reports[activeReportIndex]?.cashCount?.grandTotal || 0) + checksTotal;
  const saldoInicial = reports[activeReportIndex]?.saldoInicialCaja || 0;
  const totalIngresos = reports[activeReportIndex]?.transactions?.debit || 0;
  const totalEgresos = reports[activeReportIndex]?.transactions?.credit || 0;
  const totalIngresosManual = reports[activeReportIndex]?.transactions?.manualDebit || 0;
  const totalEgresosManual = reports[activeReportIndex]?.transactions?.manualCredit || 0;
  const saldoSegunOperaciones = saldoInicial + totalIngresos + totalIngresosManual - totalEgresos - totalEgresosManual;
  const balanceOperacionesIngreso = saldoInicial + totalIngresos + totalIngresosManual;
  const balanceOperacionesEgreso = totalEgresos + totalEgresosManual + saldoSegunOperaciones;

  const handleGetCashBoxStatus = async () => {
    const abortController = new AbortController();
    const response = await getCashBoxStatus(abortController.signal);
    if (response?.cajas) {
        const optionsCaja = response.cajas.map((caja) => ({
            value: caja.IdCaja,
            label: caja.NombreSucursal + ' - ' + caja.NombreCaja,
        }));
        setListCashBox(optionsCaja);
    } else {
        setListCashBox([]);
    }
  };

  const handleSelect = (nameInput, value) => {
    if (nameInput === "IdCaja") {
      setSelectedCaja(value);
    }
  };

  const resetReport = () => {
    setSelectedCaja('');
    setSelectedDate('');
    setHasSearched(false);
    setActiveTab('summary');
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setReports([]); 
    setHasSearched(true); 

    const abortController = new AbortController();
    const dataToSend = {
      IdCaja: selectedCaja,
      fecha: selectedDate
    };

  try {
    const response = await searchDailyReport(abortController.signal, dataToSend);

    if (response?.code === 200 && Array.isArray(response?.payload)) {
      const message = response.payload.length > 1 ? 'Informes diarios encontrados' : 'Informe diario encontrado';
      NotifyC.success(message);

      const mappedReports = response.payload.map((raw) => {
        const detalles = JSON.parse(raw.Detalles || '[]');
        const arqueo = JSON.parse(raw.Arqueo || '[]');
        const cheques = JSON.parse(raw.Cheques || '[]');

        const standardBills = [100, 50, 20, 10, 5, 1];
        const standardCoins = [1, 0.5, 0.25, 0.1, 0.05, 0.01];

        const bills = standardBills.map((denomination) => {
          const found = arqueo.find(
            (a) => a.IdTipoEfectivo === 1 && Number(a.Denominacion) === denomination
          );
          return {
            denomination,
            quantity: found ? Number(found.Cantidad) : 0,
            total: found ? Number(found.SubTotal) : 0,
          };
        });

        const coins = standardCoins.map((denomination) => {
          const found = arqueo.find(
            (a) => a.IdTipoEfectivo === 2 && Number(a.Denominacion) === denomination
          );
          return {
            denomination,
            quantity: found ? Number(found.Cantidad) : 0,
            total: found ? Number(found.SubTotal) : 0,
          };
        });

        const transactionsDetails = Array.isArray(detalles) ? detalles.map((d) => ({
          id: d.IdTransaccion,
          description: d.Detalle,
          amount: typeof d.Monto === 'number' && !isNaN(d.Monto) ? d.Monto : Number(d.Monto) || 0,
          type: d.IdTipoTransaccion === 1 ? 'debit' : 'credit',
          fromAPI: d.Detalle !== 'FALTANTE',
        })) : [];

        const mappedChecks = Array.isArray(cheques) ? cheques.map((c) => ({
          id: c.IdCheque,
          number: c.NumeroCheque || '',
          date: c.Fecha ? c.Fecha.split('T')[0] : '',
          bank: c.Banco || '',
          drawer: c.Girador || '',
          beneficiary: c.Beneficiario || '',
          amount: typeof c.Valor === 'number' && !isNaN(c.Valor) ? c.Valor : Number(c.Valor) || 0,
        })) : [];

        return {
          fechaRegistro: raw.FechaRegistro,
          fechaAperturaCaja: raw.FechaAperturaCaja,
          fechaCierreCaja: raw.FechaCierreCaja,
          usuarioCreador: raw.NombreCliente,
          caja: raw.NombreCaja || 'Desconocida',
          saldoInicialCaja: Number(raw.SaldoInicialCaja) || 0,
          transactions: {
            details: transactionsDetails,
            debit: Number(raw.TotalDebito) || 0,
            credit: Number(raw.TotalCredito) || 0,
            manualDebit:  Number(raw.TotalDebitoManual) || 0,
            manualCredit: Number(raw.TotalCreditoManual) || 0
          },
          cashCount: {
            bills,
            coins,
            totalBills: bills.reduce((sum, b) => sum + b.total, 0),
            totalCoins: coins.reduce((sum, c) => sum + c.total, 0),
            grandTotal: Number(raw.TotalEfectivo) || 0,
          },
          checks: mappedChecks,
        };
      });

      setReports(mappedReports); // Almacena todos los reportes
      setActiveReportIndex(0); // Activa el primer reporte por defecto
    } else {
      NotifyC.error('No se encontraron informes diarios');
    }
  } catch (error) {
    NotifyC.error('Error al buscar los informes');
  } finally {
    setIsLoading(false);
  }
  };

  const generateExportData = (tab = activeTab) => {
    if (!reports[activeReportIndex]) return { columns: [], data: [] };

    switch (tab) {
      case "summary":
        const debitTransactions = reports[activeReportIndex].transactions.details
          .filter(t => t.type === 'debit')
          .map(t => ({
            detalle: t.description,
            debito: formatCurrency(t.amount),
            credito: ""
          }));

        const creditTransactions = reports[activeReportIndex].transactions.details
          .filter(t => t.type === 'credit')
          .map(t => ({
            detalle: t.description,
            debito: "",
            credito: formatCurrency(t.amount)
          }));

        return {
          columns: [
            { Header: "DETALLE", accessor: "detalle" },
            { Header: "DEBITO", accessor: "debito" },
            { Header: "CREDITO", accessor: "credito" }
          ],
          data: [
            ...debitTransactions,
            { detalle: "", debito: "", credito: "" },
            ...creditTransactions,
            { detalle: "", debito: "", credito: "" },
            { detalle: "(SUB-TOTAL B/.)", 
              debito: formatCurrency(totalIngresos + totalIngresosManual), 
              credito: formatCurrency(totalEgresos + totalEgresosManual),
              rowColor: "0F4372",
              rowFontColor: "FFFFFF" 
            },
            // agregar espacio vacio
            { detalle: "", debito: "", credito: "" },
            { detalle: "TOTAL DE INGRESOS Y EGRESOS", 
              debito: formatCurrency(totalIngresos + totalIngresosManual), 
              credito: formatCurrency(totalEgresos + totalEgresosManual)
            },
            { detalle: "SALDO INICIAL", 
              debito: formatCurrency(saldoInicial), 
              credito: "- - -" 
            },
            { detalle: "SALDO SEGUN OPERACIONES PARA SIGUIENTE MES", 
              debito: "- - -", 
              credito: formatCurrency(saldoSegunOperaciones) 
            },
            { detalle: "BALANCE EN OPERACIONES", 
              debito: formatCurrency(balanceOperacionesIngreso), 
              credito: formatCurrency(balanceOperacionesEgreso)
            }
          ]
        };

      case "transactions":
        return {
          columns: [
            { Header: "Tipo", accessor: "tipo" },
            { Header: "Descripción", accessor: "descripcion" },
            { Header: "Monto", accessor: "monto" }
          ],
          data: reports[activeReportIndex].transactions.details.map(t => ({
            tipo: t.type === 'debit' ? 'DEBITO' : 'CREDITO',
            descripcion: t.description,
            monto: formatCurrency(t.amount)
          }))
        };
case "cash":
  return {
    columns: [
      { Header: "Denominación Billetes", accessor: "denominacionBilletes" },
      { Header: "Cantidad Billetes", accessor: "cantidadBilletes" },
      { Header: "Subtotal Billetes", accessor: "subtotalBilletes" },
      { Header: "Denominación Monedas", accessor: "denominacionMonedas" },
      { Header: "Cantidad Monedas", accessor: "cantidadMonedas" },
      { Header: "Subtotal Monedas", accessor: "subtotalMonedas" }
    ],
    data: reports[activeReportIndex].cashCount.bills.map((bill, index) => ({
      denominacionBilletes: formatCurrency(bill.denomination),
      cantidadBilletes: bill.quantity,
      subtotalBilletes: formatCurrency(bill.total),
      denominacionMonedas: reports[activeReportIndex].cashCount.coins[index]?.denomination
        ? `${reports[activeReportIndex].cashCount.coins[index].denomination}`
        : '',
      cantidadMonedas: reports[activeReportIndex].cashCount.coins[index]?.quantity || 0,
      subtotalMonedas: reports[activeReportIndex].cashCount.coins[index]?.total
        ? reports[activeReportIndex].cashCount.coins[index].total
        : '0.00'
    }))
  };
      case "checks":
        return {
          columns: [
            { Header: "Número", accessor: "numero" },
            { Header: "Banco", accessor: "banco" },
            { Header: "Fecha", accessor: "fecha" },
            { Header: "Girador", accessor: "girador" },
            { Header: "Beneficiario", accessor: "beneficiario" },
            { Header: "Monto", accessor: "monto" }
          ],
          data: reports[activeReportIndex].checks.map(c => ({
            numero: c.number,
            banco: c.bank,
            fecha: c.date,
            girador: c.drawer,
            beneficiario: c.beneficiary,
            monto: formatCurrency(c.amount)
          }))
        };

      default:
        return { columns: [], data: [] };
    }
  };

  const getAllExportTables = () => {
    if (!reports[activeReportIndex]) return [];

    const resumen = generateExportData("summary");
    const efectivo = generateExportData("cash");
    const cheques = generateExportData("checks");

    return [
      { nameReport: "INFORME DIARIO", columns: resumen.columns, data: resumen.data },
      { nameReport: "ARQUEO", columns: efectivo.columns, data: efectivo.data },
      { nameReport: "CHEQUES", columns: cheques.columns, data: cheques.data }
    ];
  };

  const exportReport = async ({type, getAllExportTables, nameReport, typeLog, IdSucursal, IdCaja, userRoleId = user.roleId}) => {
    try {
      const tables = getAllExportTables();
      const dataToSend = {
        IdTipoLog: typeLog,
        IdSucursal: IdSucursal,
        IdCaja: IdCaja,
      };
      const abortController = new AbortController();

      if (type === "excel") {
        exportMultipleTablesExcel(tables, nameReport, userRoleId);
      } else if (type === "pdf") {
        exportMultipleTablesPDF(tables, nameReport, userRoleId);
      } else {
        throw new Error("Tipo de exportación no soportado");
      }

      await Logs(abortController.signal, dataToSend);
    } catch (error) {
      console.error("Error al exportar el informe:", error);
    }
  };

  useEffect(() => {
    handleGetCashBoxStatus();
  }, []);

  useEffect(() => {
    if (activeReportIndex >= reports.length) {
      setActiveReportIndex(0);
    }
  }, [reports, activeReportIndex]);
 
  return ( 
  <>
      <div className="dashboardCard text-left" style={{ color: "black", marginBottom: "20px" }}>
        <div className="d-flex justify-content-between" style={{ gap: 20 }}>
          <div className="d-flex align-items-center">
            <RiHomeOfficeFill size={20} className="mr-2" />
            <h5 className="mb-0">Buscar informe diario</h5>
          </div>
        </div>
      </div>
      <div className="dashboardCard p-3" style={{ color: "black" }}>
        <div className="d-flex justify-content-between flex-wrap mb-2">
          <p className="mb-0">Filtros de búsqueda</p>
        </div>

        <div className="row">
          <div className="col-12 col-md-6 input-group text-left mb-3" style={{ width: "100%" }}>
            <div className="d-flex w-100">
              <div className="input-group-prepend d-flex align-items-center">
                <span
                  className="input-group-text text-bold input-label"
                  style={{ backgroundColor: "#FBD825", color: "black", borderRadius: "6px 0 0 6px" }}
                >
                  <BsBuilding className="mr-2" size={18} /> Caja
                </span>
              </div>
              <SelectSearch
                nameInput={"IdCaja"}
                options={listCashBox}
                setValue={handleSelect}
                value={selectedCaja}
                placeholder="Seleccione una caja"
                className="form-control"
              />
            </div>
          </div>

          <div className="col-12 col-md-6 input-group text-left mb-3" style={{ width: "100%" }}>
            <div className="input-group-prepend d-flex align-items-center">
              <span
                className="input-group-text text-bold input-label"
                style={{ backgroundColor: "#FBD825", color: "black" }}
              >
                <BsCalendar2Check className="mr-2" size={18} /> Fecha
              </span>
            </div>
            <input
              type="date"
              className="form-control"
              name="Fecha"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </div>
        <div className="d-flex justify-content-center mt-3" style={{ gap: "12px" }}>
          <button
            className="btn btn-blue"
            onClick={handleSearch}
            disabled={!selectedCaja || !selectedDate || isLoading}
          >
            <BsSearch /> Buscar
          </button>
          <button
            className="btn btn-secondary"
            onClick={resetReport}
            disabled={!hasSearched || isLoading}
          >
            <TbZoomReset size={20} /> Reiniciar
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="dashboardCard p-3">
          <Spinner type={2} />
        </div>
      )}

      {hasSearched && !isLoading && reports.length > 0 && (
        <>
        <div className="dashboardCard">
          <div className="report-tabs">
            {reports.map((_, index) => (
              <button
                key={index}
                className={`tab-button ${activeReportIndex === index ? "active" : ""}`}
                onClick={() => setActiveReportIndex(index)}
              >
                Informe #{index + 1}
              </button>
            ))}
          </div>
          <div className="report-meta">
            <div className="report-date">
              <h2>INFORME DIARIO #{activeReportIndex + 1}</h2>
            </div>

            <div className="report-caja">
              <span>
                Caja: <strong>{reports[activeReportIndex].caja}</strong>
              </span>
            </div>
            <div className="d-flex justify-content-center" style={{ gap: "12px" }}>
            <button
              className="btn btn-success"
              onClick={() =>
                exportReport({
                  type: "excel",
                  getAllExportTables,
                  nameReport: "informe_diario",
                  typeLog: 23,
                  IdSucursal: user.IdSucursal,
                  IdCaja: selectedCaja,
                  userRoleId: user.roleId
                })
              }
            >
              <FaFileExcel className="mr-2" color='white'/> Exportar a EXCEL
            </button>
            <button
              className="btn btn-primary"
              onClick={() =>
                exportReport({
                  type: "pdf",
                  getAllExportTables,
                  nameReport: "informe_diario",
                  typeLog: 24,
                  IdSucursal: user.IdSucursal,
                  IdCaja: selectedCaja,
                  userRoleId: user.roleId
                })
              }
            >
              <FaFilePdf className="mr-2" color='white'/> Exportar a PDF
            </button>
            </div>
          </div>

          <div className="report-meta">

            <div className="date-range">

              <div className="date-range-item">
                
                <span className="date-label">
                  <RiCalendar2Line className="icon" /> Período del informe:
                </span>
                <span className="date-value">
                  {sqlDateFormat(reports[activeReportIndex].fechaAperturaCaja, false, true)}
                  {"  "} <RxDividerHorizontal color='red' size={25}/> {"  "} 
                  {sqlDateFormat(reports[activeReportIndex].fechaCierreCaja, false, true)}
                </span>
              </div>
              <div className="date-range-item">
                
                <span className="date-label">
                  <RiFileList3Line className="icon" /> Registrado el:
                </span>
                <span className="date-value">
                  {sqlDateFormat(reports[activeReportIndex].fechaRegistro, false, true)}
                </span>
              </div>
              <div className="date-range-item">
                <span className="date-label">
                  <RiMoneyDollarCircleLine className="icon" /> Usuario Creador:
                </span>
                <span className="date-value">
                  {reports[activeReportIndex].usuarioCreador || 'Desconocido'}
                </span>
                </div>
            </div>
          </div>

            {/* Pestañas internas del reporte */}
            <div className="report-tabs">
              <button
                className={`tab-button ${activeTab === "summary" ? "active" : ""}`}
                onClick={() => setActiveTab("summary")}
              >
                Resumen
              </button>
              <button
                className={`tab-button ${activeTab === "transactions" ? "active" : ""}`}
                onClick={() => setActiveTab("transactions")}
              >
                Movimientos
              </button>
              <button
                className={`tab-button ${activeTab === "cash" ? "active" : ""}`}
                onClick={() => setActiveTab("cash")}
              >
                Arqueo
              </button>
              <button
                className={`tab-button ${activeTab === "checks" ? "active" : ""}`}
                onClick={() => setActiveTab("checks")}
              >
                Cheques
              </button>
            </div>

            {activeTab === "summary" && (
              <div className="summary-grid">
                {/* Resumen */}
                <div className="summary-card">
                  <div className="card-header">
                    <RiFileList3Line className="card-icon" />
                    <h3>Movimientos</h3>
                  </div>
                  <div className="card-body">
                    <div className="balance-row">
                      <span className="balance-label">Saldo Inicial:</span>
                      <span className="balance-value">
                        {reports[activeReportIndex].saldoInicialCaja.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </span>
                    </div>
                    <div className="balance-row positive">
                      <span className="balance-label">
                        <RiArrowUpLine className="icon" /> Debitos:
                      </span>
                      <span className="balance-value">
                        {reports[activeReportIndex].transactions.debit.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </span>
                    </div>
                    <div className="balance-row negative">
                      <span className="balance-label">
                        <RiArrowDownLine className="icon" /> Creditos:
                      </span>
                      <span className="balance-value">
                        {reports[activeReportIndex].transactions.credit.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="summary-card cash-card">
                <div className="card-header">
                  <RiMoneyDollarCircleLine className="card-icon" />
                  <h3>Efectivo</h3>
                </div>
                <div className="card-body">
                  <div className="cash-row">
                    <span className="cash-label">Billetes:</span>
                    <span className="cash-value">
                      {reports[activeReportIndex].cashCount.totalBills.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </span>
                  </div>
                  <div className="cash-row">
                    <span className="cash-label">Monedas:</span>
                    <span className="cash-value">
                      {reports[activeReportIndex].cashCount.totalCoins.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </span>
                  </div>
                  <div className="cash-divider"></div>
                  <div className="cash-row total">
                    <span className="cash-label">Total Efectivo:</span>
                    <span className="cash-value">
                      {reports[activeReportIndex].cashCount.grandTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="summary-card checks-card">
                <div className="card-header">
                  <RiBankCardLine className="card-icon" />
                  <h3>Cheques</h3>
                </div>
                <div className="card-body">
                  <div className="checks-row">
                    <span className="checks-label">Cantidad:</span>
                    <span className="checks-value">{reports[activeReportIndex].checks.length}</span>
                  </div>
                  <div className="checks-row">
                    <span className="checks-label">Total:</span>
                    <span className="checks-value">
                      {checksTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </span>
                  </div>
                </div>
              </div>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="transactions-section">
                <div className="section-header">
                  <RiExchangeLine className="section-icon" />
                  <h3>Detalles</h3>
                  <span className="badge">{reports[activeReportIndex].transactions.details.length}</span>
                </div>
                
                <div className="transactions-summary">
                  <div
                    className="d-flex align-center row"
                    style={{
                      gap: 10,
                      justifyContent: 'center',
                      width: '100%',
                      marginBottom: 10
                    }}
                  >
                    <div className="summary-item positive">
                      <span>Total Débitos:</span>
                      <strong>{formatCurrency(totalIngresos)}</strong>
                    </div>
                    <div className="summary-item negative">
                      <span>Total Créditos:</span>
                      <strong>{formatCurrency(totalEgresos)}</strong>
                    </div>
                    {totalIngresosManual !== 0 && (
                      <div className="summary-item positive">
                        <span>Total Débitos (Otros):</span>
                        <strong>{formatCurrency(totalIngresosManual)}</strong>
                      </div>
                    )}
                    {totalEgresosManual !== 0 && (
                      <div className="summary-item negative">
                        <span>Total Créditos (Otros):</span>
                        <strong>{formatCurrency(totalEgresosManual)}</strong>
                      </div>
                    )}
                  </div>
                  <div
                    className="d-flex align-center row"
                    style={{
                      gap: 10,
                      justifyContent: 'center',
                      width: '100%',
                      marginBottom: 10
                    }}
                  >
                    <div className="summary-item">
                      <span>Saldo Inicial Caja:</span>
                      <strong>
                        {formatCurrency(saldoInicial)}
                      </strong>
                    </div>
                    <div className="summary-item">
                      <span>Balance operaciones débito:</span>
                      <strong>
                        {formatCurrency(balanceOperacionesIngreso)}
                      </strong>
                    </div>
                    <div className="summary-item">
                      <span>Balance operaciones crédito:</span>
                      <strong>
                        {formatCurrency(balanceOperacionesEgreso)}
                      </strong>
                    </div>
                    <div className="summary-item total">
                      <span>Saldo según operaciones</span>
                      <strong>
                        {formatCurrency(saldoSegunOperaciones)}
                      </strong>
                    </div>
                  </div>

                </div>
                <div className="transactions-table" style={{overflowX:'auto'}}>
                  <table>
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Descripción</th>
                        <th>Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports[activeReportIndex].transactions.details.map((t, index) => (
                        <tr key={`${t.id || index}-${t.description}`}>
                          <td>
                            <span className={`transaction-type ${t.type}`}>
                              {t.type === 'debit' ? 'DEBITO' : 'EGRESO'}
                            </span>
                          </td>
                          <td>{t.description}</td>
                          <td className={`amount ${t.type}`}>
                            {formatCurrency(t.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'cash' && (
              <div className="cash-section">
                <div className="section-header">
                  <RiCoinsLine className="section-icon" />
                  <h3>Arqueo de Efectivo</h3>
                  <span className="badge">
                    Total: {formatCurrency(reports[activeReportIndex].cashCount.grandTotal)}
                  </span>
                </div>
                
                <div className="cash-tables">
                  <div className="cash-table"  style={{overflowX:'auto'}}>
                    <h4>Billetes</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Denominación</th>
                          <th>Cantidad</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports[activeReportIndex].cashCount.bills.map((bill, index) => (
                          <tr key={`bill-${bill.denomination}-${index}`}>
                            <td>${bill.denomination}</td>
                            <td>{bill.quantity}</td>
                            <td>{formatCurrency(bill.total)}</td>
                          </tr>
                        ))}
                        <tr className="total-row">
                          <td colSpan="2">Total Billetes:</td>
                          <td>{formatCurrency(reports[activeReportIndex].cashCount.totalBills)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="cash-table">
                    <h4>Monedas</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Denominación</th>
                          <th>Cantidad</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports[activeReportIndex].cashCount.coins.map((coin, index) => (
                          <tr key={`coin-${coin.denomination}-${index}`}>
                            <td>${coin.denomination}</td>
                            <td>{coin.quantity}</td>
                            <td>{formatCurrency(coin.total)}</td>
                          </tr>
                        ))}
                        <tr className="total-row">
                          <td colSpan="2">Total Monedas:</td>
                          <td>{formatCurrency(reports[activeReportIndex].cashCount.totalCoins)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'checks' && (
              <div className="checks-section">
                <div className="section-header">
                  <RiBankCardLine className="section-icon" />
                  <h3>Cheques Recibidos</h3>
                  <span className="badge">
                    Total: {formatCurrency(checksTotal)}
                  </span>
                </div>
                
                {reports[activeReportIndex].checks.length > 0 ? (
                  <div className="checks-table"  style={{overflowX:'auto'}}>
                    <table>
                      <thead>
                        <tr>
                          <th>Número</th>
                          <th>Banco</th>
                          <th>Fecha</th>
                          <th>Girador</th>
                          <th>Beneficiario</th>
                          <th>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports[activeReportIndex].checks.map((check, index) => (
                          <tr key={`check-${check.id || index}`}>
                            <td>{check.number}</td>
                            <td>{check.bank}</td>
                            <td>{check.date}</td>
                            <td>{check.drawer}</td>
                            <td>{check.beneficiary}</td>
                            <td>{formatCurrency(check.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-checks">
                    No se registraron cheques en este informe
                  </div>
                )}
              </div>
            )} 
          </div>
        </>
      )}
    </>);

  
};

export default SearchDailyReport;